import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { SectionEdit, SectionEditForm } from 'components/section-edit/SectionEdit';
import { useFrozenOnceLoaded } from 'components/section-edit/useFrozenOnceLoaded';
import { saveSection } from 'components/section-edit/saveSection';
import { ReactNode } from 'react';
import { Control, FieldValues } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useGetTeamByIdQuery, useUpdateTeamMutation } from 'store/api/teams';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { Team, TeamForm } from 'types/teams';
import { useClassifiers } from 'utils/useClassifiers';
import { TEAM_SECTION_FIELDS, TeamSectionId, teamDtoToForm, teamFormToRequest } from '../teamFormMapping';
import { TeamActiveField, TeamContactFields, TeamFields } from './TeamFields';
import { TEAM_FORM_CLASSIFIERS, TeamFormState, useTeamForm } from './useTeamForm';

const SECTION_FIELDS: Record<TeamSectionId, (form: TeamFormState) => ReactNode> = {
    team: (form) => (
        <>
            <TeamFields form={form} />
            <TeamActiveField form={form} />
        </>
    ),
    contact: (form) => <TeamContactFields form={form} />,
    'custom-fields': (form) => (
        <CustomFields control={form.methods.control as unknown as Control<FieldValues>} fields={form.customFields} />
    ),
};

interface SectionFormProps {
    section: TeamSectionId;
    team: Team;
    initialData: TeamForm;
    classifiers: Record<string, ClassifierData>;
    customFields: CustomField[];
    onClose: () => void;
}

function TeamSectionForm({ section, team, initialData, classifiers, customFields, onClose }: SectionFormProps) {
    // The page's own query argument, so the refetch refreshes the team the page shows.
    const { id: teamId = '' } = useParams();
    const { refetch } = useGetTeamByIdQuery(teamId);
    const [updateTeam] = useUpdateTeamMutation();
    const form = useTeamForm({ initialData, classifiers, entityCustomFields: customFields, team });

    const onSave = (values: TeamForm) =>
        saveSection(values, {
            section,
            sectionFields: TEAM_SECTION_FIELDS,
            fetchLatest: () => refetch().unwrap(),
            toForm: (latestTeam) =>
                teamDtoToForm(latestTeam, classifiers, form.customFieldValuesOf(latestTeam.customFields)),
            toRequest: (edited, latest) =>
                teamFormToRequest(edited, {
                    customFields: form.mapCustomFieldsToDto(edited as unknown as Record<string, unknown>),
                    canEditDmsTeam: form.hasBackOfficeSettingsPermission,
                    initialForm: latest,
                }),
            update: (request) => updateTeam({ ...request, id: teamId }).unwrap(),
            onSaved: onClose,
        });

    if (form.isLoading) {
        return <Loader />;
    }
    return (
        <SectionEditForm methods={form.methods} onSave={onSave} onCancel={onClose}>
            {SECTION_FIELDS[section](form)}
        </SectionEditForm>
    );
}

interface EditorProps {
    section: TeamSectionId;
    team: Team;
    onClose: () => void;
}

function TeamSectionEditor({ section, team, onClose }: EditorProps) {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: TEAM_FORM_CLASSIFIERS });
    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFields,
    } = useCustomFields({ entityType: EntityType.TEAM, fields: team.customFields });

    const initialData = useFrozenOnceLoaded(!isLoadingClassifiers && !isLoadingCustomFields, () =>
        teamDtoToForm(team, classifiers, initialCustomFieldsObject)
    );

    if (!initialData) {
        return <Loader />;
    }
    return (
        <TeamSectionForm
            section={section}
            team={team}
            initialData={initialData}
            classifiers={classifiers}
            customFields={customFields}
            onClose={onClose}
        />
    );
}

interface TeamSectionProps {
    id: TeamSectionId;
    titleId: string;
    team: Team;
    /** The read view, handed the card's Edit control. */
    children: (edit: ReactNode) => ReactNode;
    /** The read view's rows: those no field edits stay under the fields while the card edits. */
    rows?: Row[];
}

/** A card of the team page that edits in place with the team form's fields for that card. */
export function TeamSection({ id, titleId, team, children, rows }: TeamSectionProps) {
    return (
        <SectionEdit
            id={id}
            titleId={titleId}
            editor={(close) => <TeamSectionEditor section={id} team={team} onClose={close} />}
            rows={rows}
        >
            {children}
        </SectionEdit>
    );
}
